import { authGet } from '../../utils/authFetch';
import { wmServerAddress } from '../../common/MetaSetting';
import { fetchHttpResponseHandler } from '../../util/UtilFunction';
import { UIMemoTag, UITagItem } from '../../model/UiShapes';
import { handleError } from '../managers/errorHandler';

// MemoTag API的统一错误处理
const handleMemoTagApiError = (error: any, operation: string): boolean => {
  // 错误处理交给Manager层，这里只返回false
  return false;
};

// 默认的UIMemoTag对象
const defaultUIMemoTag: UIMemoTag = {
  memoId: '',
  tags: []
};

// 默认的UITagItem对象
const defaultUITagItem: UITagItem = {
  label: '',
  level: 0
};

// 数据转换函数 - 将API返回的MemoTag数据转换为UIMemoTag类型
export function convertToUIMemoTag(memoTagData: any): UIMemoTag | null {
  if (!memoTagData) {
    return null;
  }

  try {
    // 初始化UIMemoTag对象
    const uiMemoTag: UIMemoTag = {
      memoId: '',
      tags: []
    };

    // 处理API返回的包装格式（responseType/message/content结构）
    let actualData = memoTagData;
    if (memoTagData.content && typeof memoTagData.content === 'object') {
      actualData = memoTagData.content;
    }
    
    // 处理嵌套的memoTag结构（API返回格式）
    if (actualData.memoTag && typeof actualData.memoTag === 'object') {
      actualData = actualData.memoTag;
    }

    // 优先使用guid作为memoId
    if (actualData.guid) {
      uiMemoTag.memoId = actualData.guid;
    } else if (actualData.memoId) {
      uiMemoTag.memoId = actualData.memoId;
    } else if (memoTagData.guid) {
      // 如果嵌套结构中没有找到，尝试原始数据
      uiMemoTag.memoId = memoTagData.guid;
    } else if (memoTagData.memoId) {
      uiMemoTag.memoId = memoTagData.memoId;
    }
    
    // 处理嵌套的tag.content JSON字符串（新增逻辑）
    if (actualData.tag && actualData.tag.content && typeof actualData.tag.content === 'string') {
      try {
        const parsedTagContent = JSON.parse(actualData.tag.content);
        if (parsedTagContent) {
          // 检查解析后的tag content中是否有tags数组
          if (parsedTagContent.tags && Array.isArray(parsedTagContent.tags)) {
            uiMemoTag.tags = parsedTagContent.tags.map((tagItem: any) => ({
              label: tagItem.label || '',
              level: parseInt(tagItem.level) || 0,
              color: tagItem.color
            }));
          }
        }
      } catch (parseError) {
        // 解析tag.content JSON字符串失败，错误处理交给Manager层
      }
    }

    // 处理tag.content对象中的tags数组（新增逻辑）
    if (actualData.content && typeof actualData.content === 'object' && actualData.content.tags && Array.isArray(actualData.content.tags)) {
      uiMemoTag.tags = actualData.content.tags.map((tagItem: any) => ({
        label: tagItem.label || '',
        level: parseInt(tagItem.level) || 0,
        color: tagItem.color
      }));
    }

    // 备选方案：直接处理tags数组
    if (actualData.tags && Array.isArray(actualData.tags)) {
      uiMemoTag.tags = actualData.tags.map((tagItem: any) => ({
        label: tagItem.label || '',
        level: parseInt(tagItem.level) || 0,
        color: tagItem.color
      }));
    }

    // 如果以上都没有找到tags，尝试从原始数据中查找
    if (uiMemoTag.tags.length === 0 && memoTagData.tags && Array.isArray(memoTagData.tags)) {
      uiMemoTag.tags = memoTagData.tags.map((tagItem: any) => ({
        label: tagItem.label || '',
        level: parseInt(tagItem.level) || 0,
        color: tagItem.color
      }));
    }

    // 简化验证逻辑：只要有memoId就返回对象
    if (uiMemoTag.memoId) {
      return uiMemoTag;
    } else {
      // 最后尝试：如果输入数据本身有guid或memoId，直接使用
      const fallbackMemoId = memoTagData.guid || memoTagData.memoId || memoTagData.id || '';
      if (fallbackMemoId) {
        return {
          memoId: fallbackMemoId,
          tags: uiMemoTag.tags
        };
      }
      
      return null;
    }
  } catch (error) {
    // 发生异常时直接返回null，不创建默认对象，错误处理交给Manager层
    return null;
  }
}

// 批量将API返回的memo tags数据转换为UIMemoTag列表
export function convertToUIMemoTags(memoTagsData: any[]): (UIMemoTag | null)[] {
  // 如果输入不是数组，返回空数组
  if (!Array.isArray(memoTagsData)) {
    return [];
  }
  
  // 执行批量转换
  const results: (UIMemoTag | null)[] = [];
  
  for (let i = 0; i < memoTagsData.length; i++) {
    const item = memoTagsData[i];
    const convertedItem = convertToUIMemoTag(item);
    results.push(convertedItem);
  }
  
  return results;
}

// 获取特定memo的标签
export async function fetchMemoTag(memoId: string): Promise<UIMemoTag | null> {
  if (!memoId) {
    return null;
  }
  
  try {
    const response = await authGet(`${wmServerAddress}/tag/${memoId}`);

    const processedResponse = await fetchHttpResponseHandler(response);
    
    if (!processedResponse) {
      return null;
    }
    
    const memoTag = convertToUIMemoTag(processedResponse);
    
    return memoTag;
  } catch (error) {
    handleError(error instanceof Error ? error : new Error(String(error)), { context: 'memoTagAPI', showToast: false });
    return null;
  }
}

export async function fetchAllMemoTags(): Promise<UIMemoTag[] | null> {
  try {
    const response = await authGet(`${wmServerAddress}/tag/all`);
    
    const processedResponse = await fetchHttpResponseHandler(response);
    
    if (!processedResponse) {
      return null;
    }

    if (processedResponse.content) {
      if (processedResponse.content.memoTags && Array.isArray(processedResponse.content.memoTags)) {
        const memoTags = convertToUIMemoTags(processedResponse.content.memoTags);
        return memoTags.filter((tag): tag is UIMemoTag => tag !== null);
      }
      
      if (Array.isArray(processedResponse.content)) {
        const memoTags = convertToUIMemoTags(processedResponse.content);
        return memoTags.filter((tag): tag is UIMemoTag => tag !== null);
      }
      
      if (typeof processedResponse.content === 'object' && processedResponse.content.memoTags && Array.isArray(processedResponse.content.memoTags)) {
        const memoTags = convertToUIMemoTags(processedResponse.content.memoTags);
        return memoTags.filter((tag): tag is UIMemoTag => tag !== null);
      }
      
      if (typeof processedResponse.content === 'object') {
        const memoTag = convertToUIMemoTag(processedResponse.content);
        return memoTag ? [memoTag] : null;
      }
    }
    
    if (Array.isArray(processedResponse)) {
      const memoTags = convertToUIMemoTags(processedResponse);
      return memoTags.filter((tag): tag is UIMemoTag => tag !== null);
    }
    
    if (typeof processedResponse === 'object') {
      const memoTag = convertToUIMemoTag(processedResponse);
      return memoTag ? [memoTag] : null;
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Query keys
export const memoTagKeys = {
  all: ['memoTags'] as const,
  lists: () => [...memoTagKeys.all, 'list'] as const,
  list: () => [...memoTagKeys.lists()] as const,
  details: () => [...memoTagKeys.all, 'detail'] as const,
  detail: (memoId: string) => [...memoTagKeys.details(), memoId] as const,
  memoTags: (memoId: string) => [...memoTagKeys.all, 'memo', memoId] as const,
};